import {
    useCallback,
    useState, useMemo,
} from 'react'
import pick from 'lodash/pick'
import isEqual from 'lodash/isEqual'
import {
    getCollectionOptions,
    getDeliveryOptions,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.utils'

import useEnumValues from 'app/hooks/useEnumValues'
import SkyNetStepperButtons from 'app/shared-components/SkyNetStepper/SkyNetStepperButtons'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import useValidation from 'app/hooks/useValidation/useValidation'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import eventToGTM, {
    CQuoteEvents,
} from 'app/utils/api/GTM'

import useCQuoteInformationContext, {
    TCQuoteStepper,
} from '../../CQuoteInformationContext'
import getFields from './CQuoteDeliveryOptions.fields'
import useStyles from './CQuoteDeliveryOptions.styles'

export default function CQuoteDeliveryOptions({
    index,
}: Readonly<{ index: number }>) {
    const getAllowedValues = useEnumValues()
    const {
        classes,
    } = useStyles()
    const {
        value: contextValue,
        setValue: setContextValue,
        setActiveStep,
        activeStep,
    } = useCQuoteInformationContext()

    const [
        value,
        setValue,
    ] = useState<TCQuoteStepper>(contextValue)

    const deliveryOptions = useMemo(() => {
        return getDeliveryOptions({
            isGroupedLaneStatuses: contextValue?.isGroupedLaneStatuses,
            originPositioningFtl: value?.rateCardsData?.originPositioningFtl,
            originPositioningLtl: value?.rateCardsData?.originPositioningLtl,
        })
    }, [
        contextValue?.isGroupedLaneStatuses,
        value?.rateCardsData?.originPositioningFtl,
        value?.rateCardsData?.originPositioningLtl,
    ])

    const collectionOptions = useMemo(() => {
        return getCollectionOptions({
            isGroupedLaneStatuses: contextValue?.isGroupedLaneStatuses,
            destinationCollectionLtl: value?.rateCardsData?.destinationCollectionLtl,
        })
    }, [
        contextValue?.isGroupedLaneStatuses,
        value?.rateCardsData?.destinationCollectionLtl,
    ])

    const fields = useFieldsWithClassName(getFields({
        getAllowedValues,
        deliveryOptions,
        collectionOptions,
    }), classes)

    const {
        isValid,
    } = useValidation({
        fields,
        values: value?.deliveryOptions || {},
    })

    const onChange = useCallback((newValue) => {
        if (!isEqual(newValue, value?.deliveryOptions)) {
            setValue({
                ...value,
                deliveryOptions: newValue,
            })
        }
    }, [
        value,
        setValue,
    ])

    const nextAction = useCallback(() => {
        setContextValue(value)
        setActiveStep(activeStep + 1)
    }, [
        setContextValue,
        value,
        setActiveStep,
        activeStep,
    ])

    const editAction = useCallback(() => {
        eventToGTM(CQuoteEvents.CreateDeliveryOptionsEditClick)
        setActiveStep(index)
        setContextValue({
            lane: value?.lane,
            laneDetails: value?.laneDetails,
            deliveryOptions: value?.deliveryOptions,
        })
    }, [
        index,
        setActiveStep,
        setContextValue,
        value,
    ])

    const formValues = useMemo(() => {
        let laneDeliveryOptions = {
            deliveryServiceType: undefined,
            collectionServiceType: undefined,
        }

        if (value?.lane?.id) {
            laneDeliveryOptions = pick(value?.laneDetails,
                !contextValue?.isGroupedLaneStatuses ? [
                    'deliveryServiceType',
                    'collectionServiceType',
                ] : [
                    'deliveryServiceType',
                    'handoverPoint',
                    'collectionServiceType',
                    'collectionDropoffPoint',
                ])
        }

        const deliveryOptionsValues = Object.values(deliveryOptions)
        const collectionOptionsValues = Object.values(collectionOptions)
        const deliveryOption = deliveryOptionsValues.length === 1
            ? deliveryOptionsValues[0] : undefined

        if (contextValue?.isGroupedLaneStatuses) {
            return {
                ...laneDeliveryOptions,
                ...value?.deliveryOptions,
            }
        }

        return {
            ...laneDeliveryOptions,
            deliveryServiceType:
                deliveryOptionsValues.includes(laneDeliveryOptions.deliveryServiceType)
                    ? laneDeliveryOptions.deliveryServiceType : deliveryOption,
            collectionServiceType: collectionOptionsValues
                .includes(laneDeliveryOptions.collectionServiceType)
                ? laneDeliveryOptions.collectionServiceType : undefined,
            ...value?.deliveryOptions,
        }
    }, [
        collectionOptions,
        contextValue?.isGroupedLaneStatuses,
        deliveryOptions,
        value,
    ])

    return (
        <>
            <DomainObjectForm
                value={formValues}
                onChange={onChange}
                fields={fields}
                disabled={activeStep !== index || value?.nonEditable}
                name="CQuoteDeliveryOption"
                classNames={{
                    gridWrapper: classes.gridWrapper,
                    gridCardWrapper: classes.gridCardWrapper,
                }}
            />
            <SkyNetStepperButtons
                isValid={isValid}
                disabled={contextValue?.nonEditable}
                nextLabel="Continue"
                nextAction={nextAction}
                canEdit={activeStep > index && !contextValue?.nonEditable}
                editAction={editAction}
            />
        </>
    )
}
